import type { Template } from '@pdfme/common'
import type { TaxonomyItem } from '~/domains/taxonomies/types'
import {
  languageLevels,
  mobilityTypes,
  softwareLevels
} from '~/domains/taxonomies/types'
import type { AddressResponse } from '~/types/global'

const LANGUAGE_LEVEL_LABELS = Object.fromEntries(
  languageLevels.map((l) => [l.id, l.name])
)
const SOFTWARE_LEVEL_LABELS = Object.fromEntries(
  softwareLevels.map((l) => [l.id, l.name])
)
const MOBILITY_TYPE_LABELS = Object.fromEntries(
  mobilityTypes.map((m) => [m.id, m.name])
)

type ApplicantLanguageEntry = {
  language?: { name: string } | null
  languageLevel?: number | null
}

type ApplicantSoftwareEntry = {
  software?: { name: string } | null
  softwareLevel?: number | null
}

type ApplicantMobilityEntry = {
  location: string
  type?: number | null
}

type ApplicantExperienceEntry = {
  start?: string | null
  end?: string | null
  qualificationName?: string | null
  companyName?: string | null
  position?: string | null
}

type ApplicantFormationEntry = {
  diploma?: { name: string } | null
  diplomaName?: string | null
  school?: { name: string } | null
  schoolName?: string | null
  obtain?: string | null
  obtainDate?: string | null
}

export type ApplicantResumeContext = {
  firstname: string
  lastname: string
  email?: string
  phoneMobile?: string
  phoneSecondary?: string
  comment?: string | null
  address?: AddressResponse | null
  qualifications?: (TaxonomyItem<'qualifications'> & {
    parent?: TaxonomyItem<'qualifications'> | null
  })[]
  experiences?: ApplicantExperienceEntry[]
  formations?: ApplicantFormationEntry[]
  languages?: ApplicantLanguageEntry[]
  softwares?: ApplicantSoftwareEntry[]
  mobilities?: ApplicantMobilityEntry[]
}

const formatDateRange = (
  start?: string | null,
  end?: string | null
): string => {
  const from = start
    ? new Date(start).toLocaleDateString('fr-FR', {
        month: 'short',
        year: 'numeric'
      })
    : null
  const to = end
    ? new Date(end).toLocaleDateString('fr-FR', {
        month: 'short',
        year: 'numeric'
      })
    : 'en cours'
  if (!from && !end) return ''
  if (!from) return `jusqu'à ${to}`
  return `${from} – ${to}`
}

const RESUME_PLACEHOLDER_MAP: Record<
  string,
  (ctx: ApplicantResumeContext) => string
> = {
  '[applicant-firstname]': (ctx) => ctx.firstname,
  '[applicant-lastname]': (ctx) => ctx.lastname,
  '[applicant-fullname]': (ctx) => `${ctx.firstname} ${ctx.lastname}`.trim(),
  '[applicant-email]': (ctx) => ctx.email ?? '',
  '[applicant-phone]': (ctx) => ctx.phoneMobile ?? ctx.phoneSecondary ?? '',
  '[applicant-address]': (ctx) => {
    if (!ctx.address) return ''
    const { line1, line2, postcode, city } = ctx.address
    return [line1, line2, [postcode, city].filter(Boolean).join(' ')]
      .filter(Boolean)
      .join('\n')
  },
  '[applicant-comment]': (ctx) => ctx.comment ?? '',
  '[applicant-qualifications]': (ctx) =>
    ctx.qualifications
      ?.map((q) => {
        const parent = q.parent?.name ? ` (${q.parent.name})` : ''
        return `${q.name}${parent}`
      })
      .join('\n') ?? '',
  '[applicant-experiences]': (ctx) => {
    if (!ctx.experiences?.length) return ''
    return ctx.experiences
      .map((e) => {
        const dateRange = formatDateRange(e.start, e.end)
        const parts = [
          dateRange,
          e.qualificationName ?? e.position,
          e.companyName
        ].filter(Boolean)
        return parts.join(' – ')
      })
      .join('\n')
  },
  '[applicant-formations]': (ctx) => {
    if (!ctx.formations?.length) return ''
    return ctx.formations
      .map((f) => {
        const year = f.obtainDate ? new Date(f.obtainDate).getFullYear() : null
        const diploma = f.diplomaName || f.diploma?.name
        const school = f.schoolName || f.school?.name
        const obtained = f.obtain === 'oui' ? 'Obtenu' : null
        const parts = [year, diploma, school, obtained].filter(Boolean)
        return parts.join(' – ')
      })
      .join('\n')
  },
  '[applicant-languages]': (ctx) => {
    if (!ctx.languages?.length) return ''
    return ctx.languages
      .map((l) => {
        const level =
          l.languageLevel != null
            ? LANGUAGE_LEVEL_LABELS[l.languageLevel]
            : undefined
        return [l.language?.name, level].filter(Boolean).join(' – ')
      })
      .join('\n')
  },
  '[applicant-softwares]': (ctx) => {
    if (!ctx.softwares?.length) return ''
    return ctx.softwares
      .map((s) => {
        const level =
          s.softwareLevel != null
            ? SOFTWARE_LEVEL_LABELS[s.softwareLevel]
            : undefined
        return [s.software?.name, level].filter(Boolean).join(' – ')
      })
      .join('\n')
  },
  '[applicant-mobility]': (ctx) => {
    if (!ctx.mobilities?.length) return ''
    return ctx.mobilities
      .map((m) => {
        const typeLabel = m.type != null ? MOBILITY_TYPE_LABELS[m.type] : null
        return [typeLabel, m.location].filter(Boolean).join(' : ')
      })
      .join('\n')
  }
}

const resolveResumePlaceholder = (
  value: string,
  context: ApplicantResumeContext
): string => {
  let resolved = value
  for (const [placeholder, resolver] of Object.entries(
    RESUME_PLACEHOLDER_MAP
  )) {
    if (resolved.includes(placeholder)) {
      resolved = resolved.replaceAll(placeholder, resolver(context))
    }
  }
  return resolved.length === 0 ? ' ' : resolved
}

export const resolveResumeInputs = (
  template: Template,
  context: ApplicantResumeContext
): Record<string, string>[] => {
  const schemas = template.schemas
  if (!schemas || schemas.length === 0) return []

  const inputs: Record<string, string> = {}
  for (const pageSchemas of schemas) {
    const schemaList = (
      Array.isArray(pageSchemas) ? pageSchemas : Object.values(pageSchemas)
    ) as {
      name?: string
      placeholder?: string
      type?: string
      content?: string
    }[]
    for (const schema of schemaList) {
      if (schema.name) {
        inputs[schema.name] = resolveResumePlaceholder(
          schema.placeholder ?? schema.content ?? '',
          context
        )
      }
    }
  }
  if (Object.keys(inputs).length === 0) return []
  return [inputs]
}
